import { DestroyRef, Injectable, OnDestroy, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatLegacyDialog } from '@angular/material/legacy-dialog';

import { merge } from 'rxjs';

import { DiagnosticEventType } from '../models/diagnostic-profile';
import { DiagnosticBufferService } from './diagnostic-buffer.service';
import { DIAGNOSTIC_REDACT_CLASS } from './screenshot.service';

const MAX_CLASSES = 3;
const MAX_LABEL_CHARS = 64;

// Selectors whose subtree must never produce a label. Uses closest() so a
// click on a child element of a sensitive container is also protected.
const SENSITIVE_CLOSEST_SELECTORS = [
  'input[type="password"]',
  'input[autocomplete="current-password"]',
  'input[autocomplete="new-password"]',
  '[data-sensitive-value]',
];

// Selectors used to normalise event.target to the nearest interactive
// ancestor. This surfaces the button/link aria-label instead of the inner
// icon text, and also improves privacy (sensitive checks run on the
// interactive element, not its children).
const INTERACTIVE_SELECTOR =
  'button, a, [role="button"], [role="tab"], [type="submit"]';

/**
 * Returns a CSS-selector fragment for the element in the form
 * `tag(#id)(.class1.class2.class3)`. Class list is capped at
 * MAX_CLASSES to keep the selector compact and stable.
 */
function targetSelector(el: Element | null): string | undefined {
  if (!el) return undefined;
  const tag = el.tagName.toLowerCase();
  const id = el.id ? `#${el.id}` : '';
  const classes = Array.from(el.classList).slice(0, MAX_CLASSES).join('.');
  return tag + id + (classes ? `.${classes}` : '');
}

/**
 * Derives a safe human-readable label for the element.
 *
 * Returns undefined when the element is a form field (we never read
 * user-typed values), the element or an ancestor matches a sensitive
 * selector (password inputs, [data-sensitive-value]), or it lives inside
 * a .diagnostic-redact subtree. Both checks use closest() so containment
 * is properly detected regardless of click depth.
 *
 * Priority: aria-label → title → trimmed textContent (whitespace collapsed).
 * Empty string attributes (aria-label="", title="") are treated as absent
 * so the fallback chain continues to textContent.
 *
 * The second sanitizer layer (BundleSanitizerService) further scrubs any
 * residual secrets from the label before it reaches the bundle.
 */
function safeLabel(el: Element): string | undefined {
  // Never read values from form fields.
  if (
    el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    el instanceof HTMLSelectElement
  ) {
    return undefined;
  }
  // Skip elements in redacted subtrees (structural opt-out).
  if (el.closest(`.${DIAGNOSTIC_REDACT_CLASS}`)) {
    return undefined;
  }
  // Skip sensitive elements and their children (content opt-out).
  for (const sel of SENSITIVE_CLOSEST_SELECTORS) {
    if (el.closest(sel)) return undefined;
  }

  // Use || rather than ?? so empty strings ("", " ") fall through to
  // the next candidate instead of short-circuiting the chain.
  const raw =
    el.getAttribute('aria-label')?.trim() ||
    el.getAttribute('title')?.trim() ||
    el.textContent?.replace(/\s+/g, ' ').trim() ||
    '';

  const trimmed = raw.slice(0, MAX_LABEL_CHARS);
  return trimmed || undefined;
}

// Exported for spec coverage only.
export const __test = { targetSelector, safeLabel };

/**
 * Captures user interaction events as UserActionEvents in the diagnostic
 * buffer. Only low-overhead, high-signal events are captured (click,
 * submit, and Material modal open/close). keydown / scroll / mousemove are
 * intentionally excluded (privacy + perf).
 *
 * Both the legacy (`MatLegacyDialog`) and the new (`MatDialog`) Angular
 * Material dialog services are monitored so that modals opened via either
 * API produce lifecycle events in the bundle.
 *
 * For click events, event.target is first normalised to the nearest
 * interactive ancestor (button, a, [role="button"], [role="tab"]) so that
 * clicks on inner icons surface the button's aria-label rather than the
 * icon glyph.
 *
 * Modal target: the host element's localName (component selector, e.g.
 * "app-diagnostic-bundle-modal") is used instead of constructor.name
 * because production builds mangle class names (Terser); element selectors
 * are stable in all build modes.
 *
 * The service is `providedIn: 'root'` but must be eagerly bootstrapped
 * via USER_ACTION_INITIALIZER_PROVIDER in app.module.ts so the
 * document-level capture listeners are registered before the first user
 * interaction.
 *
 * Privacy: form-field values are never read. Labels are derived only from
 * aria-label / title / textContent and are omitted for sensitive elements
 * (.diagnostic-redact subtrees, password inputs, [data-sensitive-value]).
 * BundleSanitizerService applies a second redaction pass at export time.
 */
@Injectable({ providedIn: 'root' })
export class UserActionService implements OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly onClick = (event: MouseEvent): void => {
    const raw = event.target instanceof Element ? event.target : null;
    // Normalise to the nearest interactive ancestor so clicks on inner
    // icons surface the button's label rather than the icon's text glyph.
    const el = raw?.closest<Element>(INTERACTIVE_SELECTOR) ?? raw ?? null;
    this.push('click', targetSelector(el), el ? safeLabel(el) : undefined);
  };

  private readonly onSubmit = (event: Event): void => {
    const el = event.target instanceof Element ? event.target : null;
    this.push('submit', targetSelector(el));
  };

  constructor(
    private readonly buffer: DiagnosticBufferService,
    private readonly legacyDialog: MatLegacyDialog,
    private readonly newDialog: MatDialog
  ) {
    document.addEventListener('click', this.onClick, true);
    document.addEventListener('submit', this.onSubmit, true);

    // Monitor both the legacy and the new MatDialog so that modals opened
    // via either API produce lifecycle events in the bundle.
    merge(this.legacyDialog.afterOpened, this.newDialog.afterOpened)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((ref) => {
        // Use the dialog instance id (e.g. "mat-dialog-0") as the modal
        // identifier — always stable across all build modes. constructor.name
        // is not used because Terser mangles class names in production builds.
        const name = ref.id;
        this.push('modal-open', name);
        // takeUntilDestroyed with explicit destroyRef cleans up per-ref
        // subscriptions when the service is destroyed, even if the dialog
        // never closes (e.g. navigating away while a modal is open).
        ref
          .afterClosed()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => this.push('modal-close', name));
      });
  }

  ngOnDestroy(): void {
    document.removeEventListener('click', this.onClick, true);
    document.removeEventListener('submit', this.onSubmit, true);
  }

  private push(kind: string, target?: string, label?: string): void {
    this.buffer.push({
      type: DiagnosticEventType.USER_ACTION,
      timestamp: Date.now(),
      // Placeholder for type satisfaction; DiagnosticBufferService.push
      // overrides this with the active profile name.
      profile: 'default',
      payload: {
        kind,
        target,
        extra: label ? { label } : undefined,
      },
    });
  }
}
